import React, { useState } from 'react';
import { useShop } from '../context/ShopContext';
import { formatINR } from '../data/products';
import { 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  Coins, 
  Info,
  Maximize2
} from 'lucide-react';

export default function RatePriceChart() {
  const { rateHistoryData, masterMetalRates, liveGoldRates } = useShop();

  const [selectedTimeframe, setSelectedTimeframe] = useState('1M');
  const [selectedMetal, setSelectedMetal] = useState('gold22K'); // 'gold24K', 'gold22K', 'gold18K', 'silver999'
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Current base rates
  const current24K = masterMetalRates?.gold?.find((g) => g.label?.includes('24') || g.id?.includes('24'))?.rate || liveGoldRates?.gold24K || 7380;
  const current22K = masterMetalRates?.gold?.find((g) => g.label?.includes('22') || g.id?.includes('22'))?.rate || liveGoldRates?.gold22K || 6765;
  const current18K = masterMetalRates?.gold?.find((g) => g.label?.includes('18') || g.id?.includes('18'))?.rate || liveGoldRates?.gold18K || 5535;
  const currentSilver = masterMetalRates?.silver?.find((s) => s.label?.includes('999') || s.id?.includes('999'))?.rate || liveGoldRates?.silver999 || 89.5;

  // Fallback default history curve anchored to current live rates
  const fallbackHistory = {
    '1D': [
      { label: '09:00 AM', gold24K: Math.round(current24K - 18), gold22K: Math.round(current22K - 16), gold18K: Math.round(current18K - 13), silver999: Number((currentSilver - 0.4).toFixed(2)) },
      { label: '11:00 AM', gold24K: Math.round(current24K - 10), gold22K: Math.round(current22K - 9), gold18K: Math.round(current18K - 7), silver999: Number((currentSilver - 0.2).toFixed(2)) },
      { label: '01:00 PM', gold24K: Math.round(current24K + 5), gold22K: Math.round(current22K + 4), gold18K: Math.round(current18K + 3), silver999: Number((currentSilver + 0.1).toFixed(2)) },
      { label: '03:00 PM', gold24K: Math.round(current24K + 12), gold22K: Math.round(current22K + 11), gold18K: Math.round(current18K + 9), silver999: Number((currentSilver + 0.3).toFixed(2)) },
      { label: '05:00 PM', gold24K: Math.round(current24K), gold22K: Math.round(current22K), gold18K: Math.round(current18K), silver999: Number(currentSilver.toFixed(2)) },
    ],
    '1W': [
      { label: '6 Days Ago', gold24K: Math.round(current24K - 45), gold22K: Math.round(current22K - 41), gold18K: Math.round(current18K - 34), silver999: Number((currentSilver - 1.2).toFixed(2)) },
      { label: '5 Days Ago', gold24K: Math.round(current24K - 30), gold22K: Math.round(current22K - 27), gold18K: Math.round(current18K - 22), silver999: Number((currentSilver - 0.8).toFixed(2)) },
      { label: '4 Days Ago', gold24K: Math.round(current24K - 15), gold22K: Math.round(current22K - 14), gold18K: Math.round(current18K - 11), silver999: Number((currentSilver - 0.3).toFixed(2)) },
      { label: '3 Days Ago', gold24K: Math.round(current24K + 20), gold22K: Math.round(current22K + 18), gold18K: Math.round(current18K + 15), silver999: Number((currentSilver + 0.5).toFixed(2)) },
      { label: '2 Days Ago', gold24K: Math.round(current24K + 10), gold22K: Math.round(current22K + 9), gold18K: Math.round(current18K + 7), silver999: Number((currentSilver + 0.2).toFixed(2)) },
      { label: 'Yesterday', gold24K: Math.round(current24K - 8), gold22K: Math.round(current22K - 7), gold18K: Math.round(current18K - 6), silver999: Number((currentSilver - 0.1).toFixed(2)) },
      { label: 'Today', gold24K: Math.round(current24K), gold22K: Math.round(current22K), gold18K: Math.round(current18K), silver999: Number(currentSilver.toFixed(2)) },
    ],
    '1M': [
      { label: '4 Wks Ago', gold24K: Math.round(current24K - 110), gold22K: Math.round(current22K - 100), gold18K: Math.round(current18K - 82), silver999: Number((currentSilver - 2.5).toFixed(2)) },
      { label: '3 Wks Ago', gold24K: Math.round(current24K - 60), gold22K: Math.round(current22K - 55), gold18K: Math.round(current18K - 45), silver999: Number((currentSilver - 1.8).toFixed(2)) },
      { label: '2 Wks Ago', gold24K: Math.round(current24K - 20), gold22K: Math.round(current22K - 18), gold18K: Math.round(current18K - 15), silver999: Number((currentSilver - 0.7).toFixed(2)) },
      { label: '1 Wk Ago', gold24K: Math.round(current24K + 15), gold22K: Math.round(current22K + 13), gold18K: Math.round(current18K + 11), silver999: Number((currentSilver + 0.6).toFixed(2)) },
      { label: 'Current', gold24K: Math.round(current24K), gold22K: Math.round(current22K), gold18K: Math.round(current18K), silver999: Number(currentSilver.toFixed(2)) },
    ],
    '3M': [
      { label: '3 Mos Ago', gold24K: Math.round(current24K - 240), gold22K: Math.round(current22K - 220), gold18K: Math.round(current18K - 180), silver999: Number((currentSilver - 5.0).toFixed(2)) },
      { label: '2 Mos Ago', gold24K: Math.round(current24K - 150), gold22K: Math.round(current22K - 137), gold18K: Math.round(current18K - 112), silver999: Number((currentSilver - 3.2).toFixed(2)) },
      { label: '1 Mo Ago', gold24K: Math.round(current24K - 40), gold22K: Math.round(current22K - 36), gold18K: Math.round(current18K - 30), silver999: Number((currentSilver - 0.9).toFixed(2)) },
      { label: 'Current', gold24K: Math.round(current24K), gold22K: Math.round(current22K), gold18K: Math.round(current18K), silver999: Number(currentSilver.toFixed(2)) },
    ],
    '6M': [
      { label: '6 Mos Ago', gold24K: Math.round(current24K - 480), gold22K: Math.round(current22K - 440), gold18K: Math.round(current18K - 360), silver999: Number((currentSilver - 9.5).toFixed(2)) },
      { label: '4 Mos Ago', gold24K: Math.round(current24K - 310), gold22K: Math.round(current22K - 284), gold18K: Math.round(current18K - 232), silver999: Number((currentSilver - 6.2).toFixed(2)) },
      { label: '2 Mos Ago', gold24K: Math.round(current24K - 120), gold22K: Math.round(current22K - 110), gold18K: Math.round(current18K - 90), silver999: Number((currentSilver - 2.5).toFixed(2)) },
      { label: 'Current', gold24K: Math.round(current24K), gold22K: Math.round(current22K), gold18K: Math.round(current18K), silver999: Number(currentSilver.toFixed(2)) },
    ],
    '1Y': [
      { label: '1 Yr Ago', gold24K: Math.round(current24K - 920), gold22K: Math.round(current22K - 843), gold18K: Math.round(current18K - 690), silver999: Number((currentSilver - 16.0).toFixed(2)) },
      { label: '9 Mos Ago', gold24K: Math.round(current24K - 650), gold22K: Math.round(current22K - 595), gold18K: Math.round(current18K - 487), silver999: Number((currentSilver - 11.2).toFixed(2)) },
      { label: '6 Mos Ago', gold24K: Math.round(current24K - 480), gold22K: Math.round(current22K - 440), gold18K: Math.round(current18K - 360), silver999: Number((currentSilver - 9.5).toFixed(2)) },
      { label: '3 Mos Ago', gold24K: Math.round(current24K - 240), gold22K: Math.round(current22K - 220), gold18K: Math.round(current18K - 180), silver999: Number((currentSilver - 5.0).toFixed(2)) },
      { label: 'Current', gold24K: Math.round(current24K), gold22K: Math.round(current22K), gold18K: Math.round(current18K), silver999: Number(currentSilver.toFixed(2)) },
    ]
  };

  const rawPoints = (rateHistoryData && rateHistoryData[selectedTimeframe]) || fallbackHistory[selectedTimeframe] || fallbackHistory['1M'];

  const points = rawPoints.map((p) => ({
    label: p.time || p.date || p.label || '',
    value: Number(p[selectedMetal] || 0)
  }));

  const values = points.map((p) => p.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const avgVal = Math.round(values.reduce((a, b) => a + b, 0) / (values.length || 1));
  const currentVal = values[values.length - 1] || 0;
  const startVal = values[0] || 0;
  const diffVal = currentVal - startVal;
  const diffPercent = startVal > 0 ? ((diffVal / startVal) * 100).toFixed(2) : '0.00';
  const isPositive = diffVal >= 0;

  // Chart SVG Coordinates Calculation
  const width = 800;
  const height = 260;
  const paddingX = 40;
  const paddingY = 30;

  const chartWidth = width - paddingX * 2;
  const chartHeight = height - paddingY * 2;
  const range = maxVal - minVal || 1;

  const coords = points.map((p, idx) => {
    const x = paddingX + (idx / (points.length - 1 || 1)) * chartWidth;
    const y = height - paddingY - ((p.value - minVal) / range) * chartHeight;
    return { x, y, ...p };
  });

  const pathD = coords.reduce((acc, curr, idx) => {
    return idx === 0 ? `M ${curr.x} ${curr.y}` : `${acc} L ${curr.x} ${curr.y}`;
  }, '');

  const areaD = `${pathD} L ${coords[coords.length - 1].x} ${height - paddingY} L ${coords[0].x} ${height - paddingY} Z`;

  const getMetalLabel = () => {
    switch (selectedMetal) {
      case 'gold24K': return '24K Gold (999 Pure)';
      case 'gold22K': return '22K Gold (916 Standard)';
      case 'gold18K': return '18K Gold (750 Diamond Craft)';
      case 'silver999': return '999 Fine Silver';
      default: return '22K Gold';
    }
  };

  const isSilver = selectedMetal === 'silver999';
  const strokeColor = isSilver ? '#2563eb' : '#d97706';
  const fillColorTop = isSilver ? 'rgba(37, 99, 235, 0.25)' : 'rgba(217, 119, 6, 0.25)';

  return (
    <section 
      id="gold-silver-price-chart"
      className="gold-silver-price-chart-section" 
      style={{
        padding: '1.5rem 1rem 2rem',
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0'
      }}
    >
      <div className="container" style={{ maxWidth: '1240px', margin: '0 auto' }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
          padding: '1.2rem 1.5rem',
          position: 'relative'
        }}>
          {/* Top Control Bar: Metal Selector & Timeframes */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            marginBottom: '1.2rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid #f1f5f9'
          }}>
            {/* Title & Current Selected Stat */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                <Coins size={18} color={strokeColor} />
                <h3 style={{
                  margin: 0,
                  fontSize: '1.15rem',
                  fontWeight: 800,
                  color: '#1e293b'
                }}>
                  Interactive Bullion Price Chart
                </h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.35rem' }}>
                <span style={{ fontSize: '1.35rem', fontWeight: 900, color: '#0f172a' }}>
                  {isSilver ? `₹${currentVal}/g` : `${formatINR(currentVal)}/g`}
                </span>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.2rem',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: isPositive ? '#16a34a' : '#dc2626',
                  background: isPositive ? '#f0fdf4' : '#fef2f2',
                  padding: '0.15rem 0.5rem',
                  borderRadius: '3px'
                }}>
                  {isPositive ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                  {isPositive ? `+${diffVal}` : diffVal} ({isPositive ? `+${diffPercent}%` : `${diffPercent}%`})
                </span>
                <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                  in selected period
                </span>
              </div>
            </div>

            {/* Metal Switchers (24K, 22K, 18K, Silver) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {[
                { id: 'gold24K', label: '24K Gold' },
                { id: 'gold22K', label: '22K Gold' },
                { id: 'gold18K', label: '18K Gold' },
                { id: 'silver999', label: 'Silver 999' }
              ].map((m) => {
                const isActive = selectedMetal === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setSelectedMetal(m.id)}
                    style={{
                      padding: '0.38rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.76rem',
                      fontWeight: 700,
                      border: isActive ? `1.5px solid ${strokeColor}` : '1px solid #cbd5e1',
                      background: isActive ? (isSilver ? '#eff6ff' : '#fef3c7') : '#ffffff',
                      color: isActive ? (isSilver ? '#1d4ed8' : '#b45309') : '#475569',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>

            {/* Timeframe Selectors (1D, 1W, 1M, 3M, 6M, 1Y) */}
            <div style={{
              display: 'inline-flex',
              background: '#f1f5f9',
              padding: '0.2rem',
              borderRadius: '6px',
              border: '1px solid #e2e8f0'
            }}>
              {['1D', '1W', '1M', '3M', '6M', '1Y'].map((tf) => {
                const isActive = selectedTimeframe === tf;
                return (
                  <button
                    key={tf}
                    onClick={() => setSelectedTimeframe(tf)}
                    style={{
                      padding: '0.32rem 0.65rem',
                      borderRadius: '4px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      border: 'none',
                      background: isActive ? '#ffffff' : 'transparent',
                      color: isActive ? '#0f172a' : '#64748b',
                      cursor: 'pointer',
                      boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {tf}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Key Stats Bar: Period High, Low, Average */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            marginBottom: '0.8rem',
            fontSize: '0.75rem',
            color: '#64748b',
            flexWrap: 'wrap'
          }}>
            <div>
              <span>Period Low: </span>
              <strong style={{ color: '#0f172a' }}>{isSilver ? `₹${minVal}` : formatINR(minVal)}</strong>
            </div>
            <div>
              <span>Period High: </span>
              <strong style={{ color: '#0f172a' }}>{isSilver ? `₹${maxVal}` : formatINR(maxVal)}</strong>
            </div>
            <div>
              <span>Period Average: </span>
              <strong style={{ color: '#0f172a' }}>{isSilver ? `₹${avgVal}` : formatINR(avgVal)}</strong>
            </div>
            <div>
              <span>Benchmark: </span>
              <span style={{ color: '#2563eb', fontWeight: 600 }}>IBJA Daily Benchmark</span>
            </div>
          </div>

          {/* Responsive SVG Chart Container */}
          <div style={{ position: 'relative', width: '100%', height: '240px', overflow: 'hidden' }}>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              preserveAspectRatio="none"
              style={{ width: '100%', height: '100%', overflow: 'visible' }}
            >
              <defs>
                <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={fillColorTop} />
                  <stop offset="100%" stopColor="rgba(255, 255, 255, 0.0)" />
                </linearGradient>
              </defs>

              {/* Horizontal Grid Lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                const y = paddingY + ratio * chartHeight;
                const gridVal = Math.round(maxVal - ratio * range);
                return (
                  <g key={ratio}>
                    <line
                      x1={paddingX}
                      y1={y}
                      x2={width - paddingX}
                      y2={y}
                      stroke="#e2e8f0"
                      strokeDasharray="4 4"
                      strokeWidth="1"
                    />
                    <text
                      x={paddingX - 6}
                      y={y + 4}
                      textAnchor="end"
                      fontSize="10"
                      fill="#94a3b8"
                    >
                      {gridVal}
                    </text>
                  </g>
                );
              })}

              {/* Area Gradient Fill */}
              <path d={areaD} fill="url(#chartGradient)" />

              {/* Line Stroke */}
              <path
                d={pathD}
                fill="none"
                stroke={strokeColor}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Data Point Circles & Tooltip triggers */}
              {coords.map((c, idx) => (
                <g key={idx}>
                  <circle
                    cx={c.x}
                    cy={c.y}
                    r={hoveredIndex === idx ? 6 : 4}
                    fill="#ffffff"
                    stroke={strokeColor}
                    strokeWidth="2.5"
                    style={{ cursor: 'pointer', transition: 'r 0.15s ease' }}
                    onMouseEnter={() => setHoveredIndex(idx)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  />
                  {/* X Axis Label */}
                  <text
                    x={c.x}
                    y={height - 8}
                    textAnchor="middle"
                    fontSize="10"
                    fill={hoveredIndex === idx ? '#0f172a' : '#64748b'}
                    fontWeight={hoveredIndex === idx ? 'bold' : 'normal'}
                  >
                    {c.label}
                  </text>
                </g>
              ))}

              {/* Hover Tooltip Box */}
              {hoveredIndex !== null && coords[hoveredIndex] && (
                <g transform={`translate(${coords[hoveredIndex].x}, ${coords[hoveredIndex].y - 45})`}>
                  <rect
                    x="-65"
                    y="0"
                    width="130"
                    height="36"
                    rx="4"
                    fill="#1e293b"
                    filter="drop-shadow(0 4px 6px rgba(0,0,0,0.25))"
                  />
                  <text x="0" y="15" textAnchor="middle" fontSize="10" fill="#94a3b8">
                    {coords[hoveredIndex].label}
                  </text>
                  <text x="0" y="30" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#ffffff">
                    {isSilver ? `₹${coords[hoveredIndex].value}/g` : `${formatINR(coords[hoveredIndex].value)}/g`}
                  </text>
                </g>
              )}
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}
